import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

const MessagesTab = () => {
  const { user: authUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Dane do formularza
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);

  // Formularz nowej wiadomości
  const [newMessage, setNewMessage] = useState({
    tytul: '',
    tresc: '',
    priorytet: 'normalny',
    recipientType: 'all', // 'all', 'location', 'user', 'selected'
    odbiorca_login: '',
    odbiorca_location_id: '',
    selectedUsers: [] // dla wysyłki do wielu użytkowników
  });

  // Widok szczegółów wiadomości
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);

  // Pobierz użytkowników (Admin = wszyscy, ignoruj globalny filtr lokalizacji
  // dolaczany przez axios interceptor z localStorage.selectedLocation)
  const loadUsers = useCallback(async () => {
    try {
      const { data } = await api.get('/messages/users', {
        params: { location_id: 'all', all_locations: '1' }
      });
      if (data.success) {
        setUsers(data.data || []);
      }
    } catch (err) {
      console.error('Błąd ładowania użytkowników:', err);
    }
  }, []);

  // Pobierz lokalizacje
  const loadLocations = useCallback(async () => {
    try {
      const { data } = await api.get('/locations/');
      if (data.success) {
        setLocations(data.data || []);
      }
    } catch (err) {
      console.error('Błąd ładowania lokalizacji:', err);
    }
  }, []);

  // Pobierz wysłane wiadomości (admin view)
  const loadSentMessages = useCallback(async () => {
    try {
      const { data } = await api.get('/messages', { params: { folder: 'all', show_all: true } });
      if (data.success) {
        setSentMessages(data.data || []);
      }
    } catch (err) {
      console.error('Błąd ładowania wiadomości:', err);
    }
  }, []);

  useEffect(() => {
    loadUsers();
    loadLocations();
    loadSentMessages();
  }, [loadUsers, loadLocations, loadSentMessages]);

  // Wyślij wiadomość
  const sendMessage = async () => {
    if (!newMessage.tytul.trim() || !newMessage.tresc.trim()) {
      setError('Tytuł i treść są wymagane');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const nadawca = authUser?.login || 'admin';

      // Dla typu 'selected' wysyłamy do wielu użytkowników
      if (newMessage.recipientType === 'selected' && newMessage.selectedUsers.length > 0) {
        let successCount = 0;
        for (const userLogin of newMessage.selectedUsers) {
          const payload = {
            tytul: newMessage.tytul,
            tresc: newMessage.tresc,
            priorytet: newMessage.priorytet,
            nadawca_login: nadawca,
            odbiorca_login: userLogin,
            is_global: false
          };

          try {
            const { data } = await api.post('/messages', payload);
            if (data.success) successCount++;
          } catch {
            // continue sending to remaining users
          }
        }
        setSuccess(`Wysłano wiadomość do ${successCount} użytkowników`);
      } else {
        // Pojedyncza wiadomość
        const payload = {
          tytul: newMessage.tytul,
          tresc: newMessage.tresc,
          priorytet: newMessage.priorytet,
          nadawca_login: nadawca,
          is_global: newMessage.recipientType === 'all',
          odbiorca_login: newMessage.recipientType === 'user' ? newMessage.odbiorca_login : null,
          odbiorca_location_id: newMessage.recipientType === 'location' ? newMessage.odbiorca_location_id : null
        };

        const { data } = await api.post('/messages', payload);
        if (data.success) {
          setSuccess('Wiadomość wysłana pomyślnie');
        } else {
          setError(data.error || 'Błąd wysyłania');
        }
      }

      // Reset formularza
      setNewMessage({
        tytul: '',
        tresc: '',
        priorytet: 'normalny',
        recipientType: 'all',
        odbiorca_login: '',
        odbiorca_location_id: '',
        selectedUsers: []
      });
      loadSentMessages();
    } catch (err) {
      setError('Błąd wysyłania wiadomości: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Toggle użytkownika w liście wybranych
  const toggleUserSelection = (login) => {
    setNewMessage(prev => ({
      ...prev,
      selectedUsers: prev.selectedUsers.includes(login)
        ? prev.selectedUsers.filter(u => u !== login)
        : [...prev.selectedUsers, login]
    }));
  };

  // Zaznacz/odznacz wszystkich
  const toggleAllUsers = () => {
    if (newMessage.selectedUsers.length === users.length) {
      setNewMessage(prev => ({ ...prev, selectedUsers: [] }));
    } else {
      setNewMessage(prev => ({ ...prev, selectedUsers: users.map(u => u.nazwa) }));
    }
  };

  // Pobierz szczegóły wiadomości
  const viewMessageDetails = async (messageId) => {
    try {
      const { data } = await api.get(`/messages/${messageId}`);
      if (data.success) {
        setSelectedMessage(data.data);
        setShowMessageModal(true);
      }
    } catch (err) {
      console.error('Błąd pobierania szczegółów:', err);
    }
  };

  // Usuń wiadomość
  const deleteMessage = async (messageId) => {
    if (!window.confirm('Czy na pewno usunąć tę wiadomość?')) return;

    try {
      await api.delete(`/messages/${messageId}`);
      setSuccess('Wiadomość usunięta');
      loadSentMessages();
    } catch (err) {
      setError('Błąd usuwania wiadomości');
    }
  };

  // Auto-hide alerts
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const cardStyle = {
    backgroundColor: 'var(--card-bg)',
    border: '1px solid var(--border)',
    borderRadius: '0.375rem',
    boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)',
    marginBottom: '1rem'
  };

  const cardHeaderStyle = {
    padding: '0.75rem 1rem',
    borderBottom: '1px solid var(--border)',
    backgroundColor: 'var(--bg)',
    fontWeight: '600',
    color: 'var(--text-secondary)'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid var(--border)',
    borderRadius: '0.25rem',
    fontSize: '0.875rem'
  };

  const buttonStyle = {
    padding: '6px 12px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500'
  };

  return (
    <div>
      <style>{`
        @media (max-width: 900px) {
          .messages-tab-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 600px) {
          .messages-tab-modal { width: 95vw !important; max-width: 95vw !important; }
        }
      `}</style>
      {/* Alerts */}
      {success && (
        <div style={{
          padding: '0.75rem 1rem',
          marginBottom: '1rem',
          backgroundColor: 'var(--success-light)',
          border: '1px solid var(--success-light)',
          borderRadius: '0.375rem',
          color: 'var(--success)'
        }}>
          {success}
        </div>
      )}
      {error && (
        <div style={{
          padding: '0.75rem 1rem',
          marginBottom: '1rem',
          backgroundColor: 'var(--danger-light)',
          border: '1px solid var(--danger-light)',
          borderRadius: '0.375rem',
          color: 'var(--danger)'
        }}>
          {error}
        </div>
      )}

      <div className="messages-tab-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        {/* Formularz nowej wiadomości */}
        <div style={cardStyle}>
          <div style={cardHeaderStyle}>
            <span style={{ marginRight: '0.5rem' }}>✉️</span>
            Wyślij nową wiadomość
          </div>
          <div style={{ padding: '1rem' }}>
            {/* Tytuł */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500', fontSize: '0.875rem' }}>
                Tytuł wiadomości *
              </label>
              <input
                type="text"
                value={newMessage.tytul}
                onChange={(e) => setNewMessage({ ...newMessage, tytul: e.target.value })}
                style={inputStyle}
                placeholder="Wprowadź tytuł..."
              />
            </div>

            {/* Treść */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500', fontSize: '0.875rem' }}>
                Treść wiadomości *
              </label>
              <textarea
                value={newMessage.tresc}
                onChange={(e) => setNewMessage({ ...newMessage, tresc: e.target.value })}
                style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
                placeholder="Wprowadź treść wiadomości..."
              />
            </div>

            {/* Priorytet */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500', fontSize: '0.875rem' }}>
                Priorytet
              </label>
              <select
                value={newMessage.priorytet}
                onChange={(e) => setNewMessage({ ...newMessage, priorytet: e.target.value })}
                style={inputStyle}
              >
                <option value="normalny">Normalny</option>
                <option value="wazny">Ważny</option>
                <option value="pilny">Pilny</option>
              </select>
            </div>

            {/* Typ odbiorcy */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '0.875rem' }}>
                Wyślij do:
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="recipientType"
                    value="all"
                    checked={newMessage.recipientType === 'all'}
                    onChange={(e) => setNewMessage({ ...newMessage, recipientType: e.target.value })}
                    style={{ marginRight: '0.5rem' }}
                  />
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    border: '1px solid var(--success)', color: 'var(--success)' ,
                    borderRadius: '0.25rem',
                    fontSize: '0.8rem'
                  }}>
                    Wszyscy użytkownicy
                  </span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="recipientType"
                    value="location"
                    checked={newMessage.recipientType === 'location'}
                    onChange={(e) => setNewMessage({ ...newMessage, recipientType: e.target.value })}
                    style={{ marginRight: '0.5rem' }}
                  />
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    border: '1px solid var(--info)', color: 'var(--info)' ,
                    borderRadius: '0.25rem',
                    fontSize: '0.8rem'
                  }}>
                    Sklep / Lokalizacja
                  </span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="recipientType"
                    value="user"
                    checked={newMessage.recipientType === 'user'}
                    onChange={(e) => setNewMessage({ ...newMessage, recipientType: e.target.value })}
                    style={{ marginRight: '0.5rem' }}
                  />
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    border: '1px solid var(--text-muted)', color: 'var(--text-muted)' ,
                    borderRadius: '0.25rem',
                    fontSize: '0.8rem'
                  }}>
                    Pojedynczy użytkownik
                  </span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="recipientType"
                    value="selected"
                    checked={newMessage.recipientType === 'selected'}
                    onChange={(e) => setNewMessage({ ...newMessage, recipientType: e.target.value })}
                    style={{ marginRight: '0.5rem' }}
                  />
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    border: '1px solid var(--orange)', color: 'var(--orange)' ,
                    borderRadius: '0.25rem',
                    fontSize: '0.8rem'
                  }}>
                    Wybrani użytkownicy
                  </span>
                </label>
              </div>
            </div>

            {/* Wybór lokalizacji */}
            {newMessage.recipientType === 'location' && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500', fontSize: '0.875rem' }}>
                  Wybierz sklep:
                </label>
                <select
                  value={newMessage.odbiorca_location_id}
                  onChange={(e) => setNewMessage({ ...newMessage, odbiorca_location_id: e.target.value })}
                  style={inputStyle}
                >
                  <option value="">-- Wybierz sklep --</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>
                      {loc.nazwa} ({loc.kod_lokalizacji})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Wybór pojedynczego użytkownika */}
            {newMessage.recipientType === 'user' && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500', fontSize: '0.875rem' }}>
                  Wybierz użytkownika:
                </label>
                <select
                  value={newMessage.odbiorca_login}
                  onChange={(e) => setNewMessage({ ...newMessage, odbiorca_login: e.target.value })}
                  style={inputStyle}
                >
                  <option value="">-- Wybierz użytkownika --</option>
                  {users.map(user => (
                    <option key={user.id} value={user.nazwa}>
                      {user.nazwa} ({user.typ}) {user.location_nazwa ? `- ${user.location_nazwa}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Wybór wielu użytkowników */}
            {newMessage.recipientType === 'selected' && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <label style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                    Wybierz użytkowników ({newMessage.selectedUsers.length} zaznaczonych):
                  </label>
                  <button
                    type="button"
                    onClick={toggleAllUsers}
                    style={{
                      ...buttonStyle,
                      border: '1px solid var(--text-muted)', color: 'var(--text-muted)' ,
                      padding: '4px 10px',
                      fontSize: '11px'
                    }}
                  >
                    {newMessage.selectedUsers.length === users.length ? 'Odznacz wszystkich' : 'Zaznacz wszystkich'}
                  </button>
                </div>
                <div style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: '1px solid var(--border)',
                  borderRadius: '0.25rem',
                  padding: '0.5rem'
                }}>
                  {users.map(user => (
                    <label
                      key={user.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0.35rem',
                        cursor: 'pointer',
                        borderRadius: '0.25rem',
                        backgroundColor: newMessage.selectedUsers.includes(user.nazwa) ? 'var(--info-light)' : 'transparent'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={newMessage.selectedUsers.includes(user.nazwa)}
                        onChange={() => toggleUserSelection(user.nazwa)}
                        style={{ marginRight: '0.5rem' }}
                      />
                      <span style={{ flex: 1 }}>
                        {user.nazwa}
                        <span style={{
                          marginLeft: '0.5rem',
                          fontSize: '0.75rem',
                          color: 'var(--text-muted)'
                        }}>
                          ({user.typ})
                        </span>
                      </span>
                      {user.location_nazwa && (
                        <span style={{
                          fontSize: '0.7rem',
                          padding: '0.125rem 0.25rem',
                          backgroundColor: 'var(--border)',
                          borderRadius: '0.25rem'
                        }}>
                          {user.location_nazwa}
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Przycisk wysyłania */}
            <button
              onClick={sendMessage}
              disabled={loading}
              style={{
                ...buttonStyle,
                width: '100%',
                backgroundColor: loading ? 'var(--text-muted)' : 'var(--info)',
                color: '#fff' ,
                fontWeight: '600'
              }}
            >
              {loading ? 'Wysyłanie...' : 'Wyślij wiadomość'}
            </button>
          </div>
        </div>

        {/* Lista wysłanych wiadomości */}
        <div style={cardStyle}>
          <div style={cardHeaderStyle}>
            <span style={{ marginRight: '0.5rem' }}>📬</span>
            Historia wiadomości ({sentMessages.length})
          </div>
          <div style={{ padding: '1rem', maxHeight: '600px', overflowY: 'auto' }}>
            {sentMessages.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '2rem' }}>
                Brak wysłanych wiadomości
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {sentMessages.map(msg => (
                  <div
                    key={msg.id}
                    style={{
                      padding: '0.75rem',
                      border: '1px solid var(--border)',
                      borderRadius: '0.375rem',
                      backgroundColor: "var(--card-bg)",
                      borderLeft: `4px solid ${
                        msg.priorytet === 'pilny' ? 'var(--danger)' :
                        msg.priorytet === 'wazny' ? 'var(--warning)' : 'var(--success)'
                      }`
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.25rem' }}>
                      <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                        <div style={{ fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                          {msg.tytul}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                          {msg.tresc?.substring(0, 100)}{msg.tresc?.length > 100 ? '...' : ''}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.75rem' }}>
                          {msg.is_global ? (
                            <span style={{ padding: '0.125rem 0.375rem', border: '1px solid var(--success)', color: 'var(--success)' , borderRadius: '0.25rem' }}>
                              Wszyscy
                            </span>
                          ) : msg.odbiorca_location_id ? (
                            <span style={{ padding: '0.125rem 0.375rem', border: '1px solid var(--info)', color: 'var(--info)' , borderRadius: '0.25rem' }}>
                              Sklep: {msg.location_nazwa || msg.odbiorca_location_id}
                            </span>
                          ) : msg.odbiorca_login ? (
                            <span style={{ padding: '0.125rem 0.375rem', border: '1px solid var(--text-muted)', color: 'var(--text-muted)' , borderRadius: '0.25rem' }}>
                              Do: {msg.odbiorca_login}
                            </span>
                          ) : null}
                          <span style={{ color: 'var(--text-muted)' }}>
                            {new Date(msg.created_at).toLocaleString('pl-PL')}
                          </span>
                          {msg.read_count > 0 && (
                            <span style={{ padding: '0.125rem 0.375rem', backgroundColor: 'var(--border)', borderRadius: '0.25rem' }}>
                              Przeczytane: {msg.read_count}x
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.25rem', marginLeft: '0.5rem' }}>
                        <button
                          onClick={() => viewMessageDetails(msg.id)}
                          style={{
                            ...buttonStyle,
                            border: '1px solid var(--info)', color: 'var(--info)' ,
                            padding: '4px 10px',
                            fontSize: '11px'
                          }}
                          title="Szczegóły"
                        >
                          👁️
                        </button>
                        <button
                          onClick={() => deleteMessage(msg.id)}
                          style={{
                            ...buttonStyle,
                            border: '1px solid var(--danger)', color: 'var(--danger)' ,
                            padding: '4px 10px',
                            fontSize: '11px'
                          }}
                          title="Usuń"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal szczegółów wiadomości */}
      {showMessageModal && selectedMessage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="messages-tab-modal" style={{
            backgroundColor: 'var(--card-bg)',
            borderRadius: '0.5rem',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{
              padding: '1rem',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h5 style={{ margin: 0, fontWeight: '600' }}>Szczegóły wiadomości</h5>
              <button
                onClick={() => setShowMessageModal(false)}
                style={{
                  background: 'none',
                  
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: 'var(--text-muted)'
                }}
              >
                &times;
              </button>
            </div>
            <div style={{ padding: '1rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Tytuł:</strong>
                <div style={{ marginTop: '0.25rem' }}>{selectedMessage.tytul}</div>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Treść:</strong>
                <div style={{
                  marginTop: '0.25rem',
                  whiteSpace: 'pre-wrap',
                  backgroundColor: 'var(--bg)',
                  padding: '0.75rem',
                  borderRadius: '0.25rem'
                }}>
                  {selectedMessage.tresc}
                </div>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Nadawca:</strong> {selectedMessage.nadawca_login}
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Priorytet:</strong>{' '}
                <span style={{
                  padding: '0.125rem 0.375rem',
                  borderRadius: '0.25rem',
                  backgroundColor: selectedMessage.priorytet === 'pilny' ? 'var(--danger)' :
                    selectedMessage.priorytet === 'wazny' ? 'var(--warning)' : 'var(--success)',
                  color: selectedMessage.priorytet === 'wazny' ? '#000' : '#fff'
                }}>
                  {selectedMessage.priorytet}
                </span>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Data wysłania:</strong>{' '}
                {new Date(selectedMessage.created_at).toLocaleString('pl-PL')}
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Odbiorca:</strong>{' '}
                {selectedMessage.is_global ? 'Wszyscy' :
                  selectedMessage.odbiorca_login ? `Użytkownik: ${selectedMessage.odbiorca_login}` :
                  selectedMessage.location_nazwa ? `Sklep: ${selectedMessage.location_nazwa}` : '-'}
              </div>
              {selectedMessage.reads && selectedMessage.reads.length > 0 && (
                <div>
                  <strong>Przeczytane przez:</strong>
                  <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                    {selectedMessage.reads.map((read, idx) => (
                      <li key={idx} style={{ marginBottom: '0.25rem' }}>
                        {read.user_login} - {new Date(read.read_at).toLocaleString('pl-PL')}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div style={{
              padding: '1rem',
              borderTop: '1px solid var(--border)',
              textAlign: 'right'
            }}>
              <button
                onClick={() => setShowMessageModal(false)}
                style={{
                  ...buttonStyle,
                  backgroundColor: 'var(--text-muted)',
                  color: 'white'
                }}
              >
                Zamknij
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesTab;
